import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import styled from 'styled-components';
import { IconButton, List, ListItem } from '@material-ui/core';
import { ArrowBack, Clear } from '@material-ui/icons';
import {
  compose, filter, first, get, includes, intersection, join, map, size, split, toNumber,
} from 'lodash/fp';
import qs from 'query-string';

import { Spinner } from '../../../components';
import { isWebView, closeWebView } from '../../../utils';
import * as actions from '../actions';

const S = {
  Wrapper: styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
  `,
  ActionBar: styled.header`
    display: flex;
    align-items: center;
    min-height: 56px;
    margin-bottom: 1px;
    padding: 0 12px;
    background-color: ${({ theme }) => theme.palette.primary.contrastText};
  `,
  Heading: styled.h2`
    margin: 0 auto 0 32px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: 1.25rem;
    font-weight: 500;
    line-height: 1.2;
  `,
  ArrowBack: styled(ArrowBack)`
    color: ${({ theme }) => theme.palette.grey[600]};
  `,
  Clear: styled(Clear)`
    color: ${({ theme }) => theme.palette.grey[600]};
  `,
  Main: styled.main`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: auto;
    padding-top: 48px;
  `,
  Paragraph: styled.p`
    margin: 0 auto 0.75rem;
    max-width: 280px;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(14)};
    line-height: 1.43;
    text-align: center;
    word-break: keep-all;
  `,
  Children: styled(List)`
    flex-grow: 1;
    overflow: auto;
    padding: 8px 16px;
  `,
  Child: styled(ListItem)`
    display: block;
    padding: 0;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: 1rem;
    line-height: 1.5;

    :hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
  `,
  Button: styled.button`
    display: block;
    width: 100%;
    height: 100%;
    overflow: hidden;
    padding: 16px 24px;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    outline: none;
  `,
};

const Targets = ({ location }) => {
  const [target, setTarget] = useState(null);
  const [shown, setShown] = useState(false);

  const guardianContacts = useSelector((state) => state.targets.guardianContacts, shallowEqual);

  const dispatch = useDispatch();

  const matchedTargets = useMemo(() => {
    const { gcids } = qs.parse(location.search);
    const targets = compose(
      map(toNumber),
      split(','),
    )(gcids);

    return intersection(targets, map((guardianContact) => guardianContact.id, guardianContacts.data));
  }, [guardianContacts.data, location.search]);

  useEffect(() => {
    dispatch(actions.fetchGuardianContactsByUserRequest());
  }, [dispatch]);

  useEffect(() => {
    if (guardianContacts.data) {
      if (size(matchedTargets) > 1) {
        setShown(true);
      } else {
        setTarget(first(matchedTargets));
      }
    }
  }, [guardianContacts, location.search, matchedTargets]);

  if (target) {
    const { sid, nid } = qs.parse(location.search);

    return (
      <Redirect
        to={{
          pathname: `/schools/${sid}/school-newsletters/${nid}`,
          search: `?${qs.stringify({ gcid: target })}`,
        }}
      />
    );
  }

  return (
    shown
      ? (
        <S.Wrapper>
          <S.ActionBar>
            <IconButton
              component={Link}
              to={{
                pathname: '/school-newsletters',
                state: { from: location.pathname },
              }}
              size="small"
            >
              <S.ArrowBack />
            </IconButton>
            <S.Heading>응답 대상 선택</S.Heading>
            {isWebView() && (
              <IconButton type="button" size="small" onClick={closeWebView}>
                <S.Clear />
              </IconButton>
            )}
          </S.ActionBar>
          <S.Main>
            <S.Paragraph>
              가정통신문 응답을 기록할 자녀를 선택해주세요.
            </S.Paragraph>
            <S.Children>
              {compose(
                map((guardianContact) => (
                  <S.Child key={guardianContact.id}>
                    <S.Button type="button" onClick={() => setTarget(guardianContact.id)}>
                      {join('', [
                        get('school', guardianContact.contact) ? `${get('school', guardianContact.contact)} ` : '',
                        guardianContact.contact.class_group
                          ? `${guardianContact.contact.class_group.grade}학년 ${guardianContact.contact.class_group.class_name}반 ${guardianContact.contact.name}`
                          : `${get('is_unspecified_group', first(guardianContact.contact.custom_groups))
                            ? '미지정' : get('name', first(guardianContact.contact.custom_groups))} ${guardianContact.contact.name}`,
                      ])}
                    </S.Button>
                  </S.Child>
                )),
                filter((guardianContact) => includes(guardianContact.id, matchedTargets)),
              )(guardianContacts.data)}
            </S.Children>
          </S.Main>
        </S.Wrapper>
      )
      : (<Spinner />)
  );
};

Targets.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
};

export default Targets;
